import React from 'react'
import { Box, Tab, Tabs, Typography } from '@mui/material'
import { useForm, UseFormReturn } from 'react-hook-form'
import { LockIconDark } from '../../../../assets/icons/LockIconDark'
import { inputProperties } from '../../../../types/inputProperties'
import { PaymentMethodType } from '../../../../types/paymentMethod'
import { TabType } from '../components/PaymentMethodTypes'
import CardPaymentMethod from './CardPaymentMethod'

const defaultStyles = {
  PaymentMethodsContainer: {
    width: '100%',
  },
  instructionLabel: {
    marginBottom: '14px',
    textTransform: 'uppercase',
    fontWeight: '600',
    lineHeight: '22px',
    fontSize: '16px',
    color: '#000000',
  },
  tabTitle: {
    textTransform: 'none',
    color: '#5D5D5D',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '20px',
    fontSize: '16px',
    '&.Mui-selected': {
      color: '#1D1D1D',
      fontWeight: '600',
    },
  },
  tabButtons: {
    borderBottom: 1,
    borderColor: 'divider',
  },
  tabPanel: {
    padding: '24px',
  },
}

/**
 * @param {string} id
 * @param {string} title
 * @param {TabType} fillerComponent
 */
interface TabProps {
  id: string
  title: string
  fillerComponent: TabType
}

/**
 * @interface TabPanelProps
 * @param {React.ReactNode} children (Optional) - the content components of the panel
 * @param {number} index (Required) - The index of the tab within the tabbed panel
 * @param {number} value (Required) - The value of the tab
 */
interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

/**
 * @param {TabPanelProps} props
 * @param {React.ReactNode} props.children (Optional) - the content components of the panel
 * @param {number} props.index (Required) - The index of the tab within the tabbed panel
 * @param {number} props.value (Required) - The value of the tab
 */
const TabPanel = (props: TabPanelProps) => (
  <div
    role='tabpanel'
    hidden={props.value !== props.index}
    id={`payment-method-tabpanel-${props.index}`}
  >
    {props.value === props.index && (
      <Box style={defaultStyles.tabPanel}>
        {props.children}
        <p>
          <LockIconDark /> Payment method information is securely stored in a
          digital vault using AES 256-bit encryption
        </p>
      </Box>
    )}
  </div>
)

/**
 * @interface TabbedPaymentMethodProps
 * @param {inputProperties} props.line1 (optional) - properties for field
 * @param {inputProperties} props.line2 (optional) - properties for field
 * @param {inputProperties} props.city (optional) - properties for field
 * @param {inputProperties} props.state (optional) - properties for field
 * @param {inputProperties} props.postalCode (optional) - properties for field
 * @param {inputProperties} props.country (optional) - properties for field
 * @param {inputProperties} cardholderName (optional) - properties for field
 * @param {inputProperties} cardNumber (optional) - properties for field
 * @param {inputProperties} cvv (optional) - properties for field
 * @param {inputProperties} exp (optional) - properties for field
 * @param {UseFormReturn} form (Optional) - hook for managing forms with ease
 * @param {string} titleLabel (Optional) - The title label applied to the form
 * @param {Array<TabProps>} tabs (Optional) - The constituent tabs of the tabbed form
 */
export interface TabbedPaymentMethodProps {
  line1?: inputProperties
  line2?: inputProperties
  city?: inputProperties
  state?: inputProperties
  postalCode?: inputProperties
  country?: inputProperties
  cardholderName?: inputProperties
  cardNumber?: inputProperties
  cvv?: inputProperties
  exp?: inputProperties
  form?: UseFormReturn
  titleLabel?: string
  tabs?: Array<TabProps>
}

/**
 * @param {TabbedPaymentMethodProps} props
 * @param {inputProperties} props.line1 (optional) - properties for field
 * @param {inputProperties} props.line2 (optional) - properties for field
 * @param {inputProperties} props.city (optional) - properties for field
 * @param {inputProperties} props.state (optional) - properties for field
 * @param {inputProperties} props.postalCode (optional) - properties for field
 * @param {inputProperties} props.country (optional) - properties for field
 * @param {inputProperties} props.cardholderName (optional) - properties for field
 * @param {inputProperties} props.cardNumber (optional) - properties for field
 * @param {inputProperties} props.cvv (optional) - properties for field
 * @param {inputProperties} props.exp (optional) - properties for field
 * @param {UseFormReturn} props.form (Optional) - hook for managing forms with ease
 * @param {string} props.titleLabel (Optional) - The title label applied to the form
 * @param {Array<TabProps>} props.tabs (Optional) - The constituent tabs of the tabbed form
 */
const TabbedPaymentMethod = (props: TabbedPaymentMethodProps) => {
  const useFormDefault = props.form || useForm()
  const { reset } = useFormDefault
  const { CARD } = PaymentMethodType

  interface tabComponentMap {
    [key: string]: JSX.Element
  }

  const TAB_COMPONENT_MAP: tabComponentMap = {
    [CARD]: (
      <CardPaymentMethod
        line1={props.line1}
        line2={props.line2}
        city={props.city}
        state={props.state}
        postalCode={props.postalCode}
        country={props.country}
        cardholderName={props.cardholderName}
        cardNumber={props.cardNumber}
        cvv={props.cvv}
        exp={props.exp}
        form={useFormDefault}
      />
    ),
  }

  const [tabValue, setTabValue] = React.useState(0)

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
    //Reset de form when the user changes the tab to clean fields
    reset()
  }
  return (
    <>
      <Typography sx={defaultStyles.instructionLabel}>
        {props.titleLabel}
      </Typography>
      <Box style={defaultStyles.PaymentMethodsContainer}>
        <Box style={defaultStyles.tabButtons}>
          <Tabs value={tabValue} onChange={handleChange}>
            {props.tabs?.map((tab, index) => (
              <Tab
                label={tab.title}
                key={index}
                id={tab.id}
                sx={defaultStyles.tabTitle}
                disableRipple
              />
            ))}
          </Tabs>
        </Box>
        {props.tabs?.map((tab, index) => (
          <TabPanel value={tabValue} key={index} index={index}>
            {TAB_COMPONENT_MAP[tab.fillerComponent]}
          </TabPanel>
        ))}
      </Box>
    </>
  )
}

TabbedPaymentMethod.defaultProps = {
  titleLabel: 'Payment Method',
  tabs: [
    {
      id: 'card',
      title: 'Pay with Card',
      fillerComponent: PaymentMethodType.CARD,
    },
  ],
}

export default TabbedPaymentMethod
